import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import './AdminDashboard.css';

// Get the main platform URL based on environment
const getMainPlatformUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  // Use the new platform subdomain
  return 'https://ai.app.platform.halisoft.biz';
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');

  const loadAdminData = useCallback(async () => {
    try {
      setLoading(true);

      // Ensure admin email is persisted when coming from magic link (?email=...)
      const urlParams = new URLSearchParams(window.location.search);
      const urlEmail = urlParams.get('email');
      if (urlEmail) {
        localStorage.setItem('halisoft_admin_email', urlEmail);
      }

      const [customersRes, statsRes] = await Promise.all([
        api.get('/api/admin/customers'),
        api.get('/api/admin/stats')
      ]);

      setCustomers(customersRes.data.customers || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error loading admin data:', err);
      if (err.response?.status === 403) {
        alert('Access denied. Admin privileges required. Your account may not have admin access.');
        // Redirect to dashboard instead of login (since user is already logged in)
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const handleChangePlan = async (customerId, newPlanId, module) => {
    const planStr = String(newPlanId || '');
    const moduleLabel = module === 'risk-analysis' ? 'Risk Analysis' : module === 'hali-leads' ? 'HaliLeads' : module;
    if (!window.confirm(`Change ${moduleLabel} plan to ${planStr.toUpperCase()}?`)) {
      return;
    }

    try {
      await api.post(`/api/admin/customers/${customerId}/change-plan`, {
        planId: newPlanId,
        module
      });

      alert('Plan changed successfully!');
      loadAdminData();
    } catch (err) {
      console.error('Error changing plan:', err);
      alert('Error changing plan: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleAdjustQuota = async (customerId, module) => {
    const amount = window.prompt('Enter quota adjustment amount (positive or negative):');
    if (!amount) return;

    try {
      await api.post(`/api/admin/customers/${customerId}/adjust-quota`, {
        adjustment: parseInt(amount, 10),
        module
      });

      alert('Quota adjusted successfully!');
      loadAdminData();
    } catch (err) {
      console.error('Error adjusting quota:', err);
      alert('Error adjusting quota: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleResetQuota = async (customerId, module) => {
    if (!window.confirm('Reset quota to plan limit?')) {
      return;
    }

    try {
      await api.post(`/api/admin/customers/${customerId}/reset-quota`, { module });
      alert('Quota reset successfully!');
      loadAdminData();
    } catch (err) {
      console.error('Error resetting quota:', err);
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSuspendAccount = async (customerId, suspend) => {
    const action = suspend ? 'suspend' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${action} this account?`)) {
      return;
    }

    try {
      await api.post(`/api/admin/customers/${customerId}/${action}`);
      alert(`Account ${action}d successfully!`);
      loadAdminData();
    } catch (err) {
      console.error(`Error ${action}ing account:`, err);
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteAccount = async (customerId) => {
    const confirmation = window.prompt(
      'Type "DELETE" to confirm permanent deletion of this account:'
    );

    if (confirmation !== 'DELETE') {
      alert('Deletion cancelled.');
      return;
    }

    try {
      await api.delete(`/api/admin/customers/${customerId}`);
      alert('Account deleted successfully!');
      loadAdminData();
      setSelectedCustomer(null);
    } catch (err) {
      console.error('Error deleting account:', err);
      alert('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
      localStorage.removeItem('halisoft_user');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Filter customers
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch =
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.companyName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPlan =
      filterPlan === 'all' ||
      customer.subscriptions?.['risk-analysis']?.plan?.id === filterPlan ||
      customer.subscriptions?.['hali-leads']?.plan?.id === filterPlan ||
      customer.subscription?.plan?.id === filterPlan;

    return matchesSearch && matchesPlan;
  });

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-large"></div>
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div 
            className="admin-logo" 
            onClick={() => window.location.href = `${getMainPlatformUrl()}/halilab/dashboard`}
            style={{ cursor: 'pointer' }}
          >
            <img src="/logo-halilab-white.svg" alt="HaliLab" className="admin-logo-img" />
            <h1>Admin Dashboard</h1>
          </div>
          <div className="admin-header-actions">
            <button 
              onClick={() => window.location.href = `${getMainPlatformUrl()}/platform/dashboard`} 
              className="btn-back-platform"
            >
              ← Back to Platform
            </button>
            <button onClick={() => navigate('/admin/settings')} className="btn-settings">
              ⚙️ Plans & Pricing
            </button>
            <button onClick={handleLogout} className="btn-logout">
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats Overview */}
      {stats && (
        <div className="admin-stats">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalCustomers || 0}</div>
              <div className="stat-label">Total Customers</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalRevenue || '0'}€</div>
              <div className="stat-label">Monthly Revenue</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalUsage || 0}</div>
              <div className="stat-label">Total Analyses</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-value">{stats.activeSubscriptions || 0}</div>
              <div className="stat-label">Active Subscriptions</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="admin-filters">
        <input
          type="text"
          placeholder="🔍 Search by email or company..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />

        <select
          value={filterPlan}
          onChange={(e) => setFilterPlan(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Plans</option>
          <option value="free">FREE</option>
          <option value="starter">STARTER</option>
          <option value="pro">PRO</option>
        </select>

        <button onClick={loadAdminData} className="btn-refresh">
          🔄 Refresh
        </button>
      </div>

      {/* Customers Table */}
      <div className="admin-content">
        <div className="customers-list">
          <h2>Customers ({filteredCustomers.length})</h2>

          <table className="customers-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Quota</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <tr
                  key={customer.id}
                  onClick={() => setSelectedCustomer(customer)}
                  className={selectedCustomer?.id === customer.id ? 'selected' : ''}
                >
                  <td>
                    <strong>{customer.companyName || 'N/A'}</strong>
                  </td>
                  <td>{customer.email}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <span className={`badge badge-${customer.subscriptions?.['risk-analysis']?.plan?.id || 'free'}`}>
                        Risk: {customer.subscriptions?.['risk-analysis']?.plan?.name || 'FREE'}
                      </span>
                      <span className={`badge badge-${customer.subscriptions?.['hali-leads']?.plan?.id || 'free'}`}>
                        Leads: {customer.subscriptions?.['hali-leads']?.plan?.name || 'FREE'}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="quota-mini">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '12px', opacity: 0.8 }}>Risk</div>
                          {customer.quotas?.['risk-analysis']?.remaining || 0} / {customer.quotas?.['risk-analysis']?.limit || 0}
                          <div className="quota-bar-mini">
                            <div
                              className="quota-fill-mini"
                              style={{
                                width: `${Math.min(
                                  ((customer.quotas?.['risk-analysis']?.used || 0) / (customer.quotas?.['risk-analysis']?.limit || 1)) * 100,
                                  100
                                )}%`,
                              }}
                            />
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', opacity: 0.8 }}>Leads</div>
                          {customer.quotas?.['hali-leads']?.remaining || 0} / {customer.quotas?.['hali-leads']?.limit || 0}
                          <div className="quota-bar-mini">
                            <div
                              className="quota-fill-mini"
                              style={{
                                width: `${Math.min(
                                  ((customer.quotas?.['hali-leads']?.used || 0) / (customer.quotas?.['hali-leads']?.limit || 1)) * 100,
                                  100
                                )}%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        customer.status === 'active' ? 'status-active' : 'status-suspended'
                      }`}
                    >
                      {customer.status || 'active'}
                    </span>
                  </td>
                  <td>{new Date(customer.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCustomer(customer);
                      }}
                      className="btn-action"
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredCustomers.length === 0 && (
            <div className="no-customers">
              <p>No customers found</p>
            </div>
          )}
        </div>

        {/* Customer Details Sidebar */}
        {selectedCustomer && (
          <div className="customer-details">
            <div className="details-header">
              <h2>Customer Details</h2>
              <button
                onClick={() => setSelectedCustomer(null)}
                className="btn-close"
              >
                ✕
              </button>
            </div>

            <div className="details-content">
              {/* Customer Info */}
              <div className="detail-section">
                <h3>👤 Account Info</h3>
                <div className="detail-row">
                  <span className="detail-label">Company:</span>
                  <span className="detail-value">{selectedCustomer.companyName}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{selectedCustomer.email}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">User ID:</span>
                  <span className="detail-value">{selectedCustomer.id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Joined:</span>
                  <span className="detail-value">
                    {new Date(selectedCustomer.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Subscription Info */}
              <div className="detail-section">
                <h3>💳 Subscriptions (per agent)</h3>

                {/* Risk Analysis */}
                <div className="detail-row">
                  <span className="detail-label">Risk Analysis:</span>
                  <span className="detail-value">
                    <span className={`badge badge-${selectedCustomer.subscriptions?.['risk-analysis']?.plan?.id || 'free'}`}>
                      {selectedCustomer.subscriptions?.['risk-analysis']?.plan?.name || 'FREE'}
                    </span>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Risk price:</span>
                  <span className="detail-value">
                    {selectedCustomer.subscriptions?.['risk-analysis']?.plan?.price || 0}€/month
                  </span>
                </div>
                <div className="action-group">
                  <h4>Change Risk plan:</h4>
                  <div className="plan-buttons">
                    <button onClick={() => handleChangePlan(selectedCustomer.id, 'free', 'risk-analysis')} className="btn-plan btn-plan-free">FREE</button>
                    <button onClick={() => handleChangePlan(selectedCustomer.id, 'starter', 'risk-analysis')} className="btn-plan btn-plan-starter">STARTER</button>
                    <button onClick={() => handleChangePlan(selectedCustomer.id, 'pro', 'risk-analysis')} className="btn-plan btn-plan-pro">PRO</button>
                  </div>
                </div>

                <hr style={{ opacity: 0.25 }} />

                {/* HaliLeads */}
                <div className="detail-row">
                  <span className="detail-label">HaliLeads:</span>
                  <span className="detail-value">
                    <span className={`badge badge-${selectedCustomer.subscriptions?.['hali-leads']?.plan?.id || 'free'}`}>
                      {selectedCustomer.subscriptions?.['hali-leads']?.plan?.name || 'FREE'}
                    </span>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Leads price:</span>
                  <span className="detail-value">
                    {selectedCustomer.subscriptions?.['hali-leads']?.plan?.price || 0}€/month
                  </span>
                </div>
                <div className="action-group">
                  <h4>Change Leads plan:</h4>
                  <div className="plan-buttons">
                    <button onClick={() => handleChangePlan(selectedCustomer.id, 'free', 'hali-leads')} className="btn-plan btn-plan-free">FREE</button>
                    <button onClick={() => handleChangePlan(selectedCustomer.id, 'starter', 'hali-leads')} className="btn-plan btn-plan-starter">STARTER</button>
                    <button onClick={() => handleChangePlan(selectedCustomer.id, 'pro', 'hali-leads')} className="btn-plan btn-plan-pro">PRO</button>
                  </div>
                </div>
              </div>

              {/* Quota Management */}
              <div className="detail-section">
                <h3>📊 Quota Management</h3>
                <div className="detail-row">
                  <span className="detail-label">Risk (used/limit):</span>
                  <span className="detail-value">
                    {selectedCustomer.quotas?.['risk-analysis']?.used || 0} / {selectedCustomer.quotas?.['risk-analysis']?.limit || 0}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Leads (used/limit):</span>
                  <span className="detail-value">
                    {selectedCustomer.quotas?.['hali-leads']?.used || 0} / {selectedCustomer.quotas?.['hali-leads']?.limit || 0}
                  </span>
                </div>

                <div className="action-group">
                  <button onClick={() => handleAdjustQuota(selectedCustomer.id, 'risk-analysis')} className="btn-action-full">
                    ➕ Adjust Risk Quota
                  </button>
                  <button onClick={() => handleResetQuota(selectedCustomer.id, 'risk-analysis')} className="btn-action-full">
                    🔄 Reset Risk Quota
                  </button>
                  <button onClick={() => handleAdjustQuota(selectedCustomer.id, 'hali-leads')} className="btn-action-full">
                    ➕ Adjust Leads Quota
                  </button>
                  <button onClick={() => handleResetQuota(selectedCustomer.id, 'hali-leads')} className="btn-action-full">
                    🔄 Reset Leads Quota
                  </button>
                </div>
              </div>

              {/* Account Actions */}
              <div className="detail-section">
                <h3>⚙️ Account Actions</h3>

                {selectedCustomer.status === 'active' ? (
                  <button
                    onClick={() => handleSuspendAccount(selectedCustomer.id, true)}
                    className="btn-action-full btn-warning"
                  >
                    ⏸️ Suspend Account
                  </button>
                ) : (
                  <button
                    onClick={() => handleSuspendAccount(selectedCustomer.id, false)}
                    className="btn-action-full btn-success"
                  >
                    ▶️ Reactivate Account
                  </button>
                )}

                <button
                  onClick={() => handleDeleteAccount(selectedCustomer.id)}
                  className="btn-action-full btn-danger"
                >
                  🗑️ Delete Account
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
